import { useId } from "react";
import { generatePath, Link, useParams } from "react-router";

import type { InputMaybe } from "@/lib/gql/__generated__/graphql";
import { StockTypeTarget } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForNodeType } from "@/components/features/_model/types";
import { QuestionSectionWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/_layouts/item-wrapper";

import { Button } from "@/components/ui/button/button";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";

type Props = {
	maxDailyStockNum: ForNodeType<InputMaybe<number> | undefined> | undefined;
	stockTypeTarget: ForNodeType<InputMaybe<StockTypeTarget> | undefined> | undefined;
	onSetMaxDailyStockNum: (input: number | undefined) => void;
	onSetStockTypeTarget: (input: StockTypeTarget) => void;
	isEdited: boolean;
	hasError: boolean;
};

export const QuestionPlanStockTarget = ({
	maxDailyStockNum,
	stockTypeTarget,
	onSetMaxDailyStockNum: _onSetMaxDailyStockNum,
	onSetStockTypeTarget: _onSetStockTypeTarget,
	isEdited,
	hasError,
}: Props) => {
	const { hotelID } = useParams<{ hotelID: string }>();
	const planStockPath = generatePath(PATHS.HOTEL.ITEMS.PLAN.STOCK, { hotelID: hotelID! });

	const uniqueID = useId();
	const onSetMaxDailyStockNum = (e: number | undefined) => {
		_onSetMaxDailyStockNum(e);
	};

	const stockTypeTargetOptions = [
		{
			id: `stockTypeTarget_0_${uniqueID}`,
			label: "人",
			value: StockTypeTarget.Person,
		},
		{
			id: `stockTypeTarget_1_${uniqueID}`,
			label: "部屋",
			value: StockTypeTarget.Room,
		},
		{
			id: `stockTypeTarget_2_${uniqueID}`,
			label: "団体",
			value: StockTypeTarget.Group,
		},
	] satisfies FormOptionType[];

	const onSetStockTypeTarget = (e: string) => {
		_onSetStockTypeTarget(e as StockTypeTarget);
	};

	const guide =
		"このプランの1日に提供できる数に限りがある場合は上限数と単位を入力してください。 部屋の在庫数で在庫を管理する場合はこちらを入力していただく必要はありません。";

	return (
		<FeatureIslandCategoryQuestionForMain
			required={false}
			title={"１日に提供できる在庫数"}
			guide={guide}
			isEdited={isEdited}
			hasError={hasError}
			inputChildren={
				<>
					<div>
						<p className="text-sm text-error-600">
							※ 日ごとの在庫を
							<Button variant="link-blue" asChild className="p-0">
								<Link
									to={planStockPath}
									className="mx-1 font-bold underline-offset-4 hover:underline"
									viewTransition
									target="_blank"
								>
									在庫管理画面
								</Link>
							</Button>
							で設定している場合はそちらが優先されますのでご注意ください。
						</p>
					</div>
					<QuestionSectionWrapper>
						<p>１日あたり</p>
						<InputNumberField
							value={maxDailyStockNum?.value ?? undefined}
							handleInput={onSetMaxDailyStockNum}
							errorMessage={maxDailyStockNum?.error}
							hiddenSpinButton={false}
						/>
						<SelectField
							value={stockTypeTarget?.value}
							options={stockTypeTargetOptions}
							handleValueChange={onSetStockTypeTarget}
							errorMessage={stockTypeTarget?.error}
						/>
						<p>まで</p>
					</QuestionSectionWrapper>
				</>
			}
		/>
	);
};
